import { useEffect, useRef, useState } from 'react';

/**
 * TapSparkline — Sep 2026. Tiny SVG trend line with a tap / hover readout.
 *
 * The SI-trend and short-volume sparklines in the Stock Metrics tab were
 * static paths: no values on hover, nothing at all on a phone. This
 * renders the same line and adds a scrubber — mouse hover OR finger tap /
 * drag snaps to the nearest point and prints its label + value under the
 * chart. Touch selection is sticky until the user taps elsewhere.
 *
 * points: [{ label: string, value: number, display: string }]
 */
export default function TapSparkline({
  points,
  w = 220,
  h = 40,
  strokeClass = 'text-rose-500 dark:text-rose-400',
  dotClass = 'fill-rose-500 dark:fill-rose-400',
  ariaLabel,
  testId,
}) {
  const svgRef = useRef(null);
  const [active, setActive] = useState(null); // index | null
  const [sticky, setSticky] = useState(false);

  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = points.length > 1 ? w / (points.length - 1) : w;
  const coords = points.map((p, i) => ({
    x: i * step,
    y: h - ((p.value - min) / range) * (h - 6) - 3,
  }));
  const path = coords.map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`).join(' ');
  const last = coords[coords.length - 1];

  const pick = (clientX) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    setActive(Math.round(frac * (points.length - 1)));
  };

  const onPointerDown = (e) => {
    pick(e.clientX);
    if (e.pointerType !== 'mouse') setSticky(true);
  };
  const onPointerMove = (e) => {
    if (e.pointerType === 'mouse' || e.buttons > 0) pick(e.clientX);
  };
  const onPointerLeave = (e) => {
    if (e.pointerType === 'mouse' && !sticky) setActive(null);
  };

  useEffect(() => {
    if (!sticky) return undefined;
    const onDoc = (e) => {
      if (svgRef.current?.contains(e.target)) return;
      setSticky(false);
      setActive(null);
    };
    document.addEventListener('pointerdown', onDoc, { passive: true });
    return () => document.removeEventListener('pointerdown', onDoc);
  }, [sticky]);

  const sel = active != null ? points[active] : null;
  const selC = active != null ? coords[active] : null;

  return (
    <div data-testid={testId} data-no-tap-tip>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${w} ${h}`}
        width="100%"
        height={h}
        className="overflow-visible cursor-crosshair select-none"
        style={{ touchAction: 'pan-y' }}
        aria-label={ariaLabel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerLeave={onPointerLeave}
      >
        <path d={path} fill="none" stroke="currentColor" strokeWidth="1.5" className={strokeClass} />
        <circle cx={last.x} cy={last.y} r="2.5" className={dotClass} />
        {selC && (
          <>
            <line x1={selC.x} x2={selC.x} y1={0} y2={h} stroke="currentColor" strokeWidth="0.75" strokeDasharray="2 2" className="text-muted-foreground/60" />
            <circle cx={selC.x} cy={selC.y} r="3.5" className={dotClass} stroke="white" strokeWidth="1" />
          </>
        )}
      </svg>
      <div
        className="h-4 mt-0.5 text-[10px] font-mono text-foreground flex items-center justify-center"
        data-testid={testId ? `${testId}-readout` : undefined}
        aria-live="polite"
      >
        {sel ? (
          <span><span className="text-muted-foreground">{sel.label}</span> · <span className="font-bold">{sel.display}</span></span>
        ) : (
          <span className="text-muted-foreground/60 italic">Tap or hover the line for values</span>
        )}
      </div>
    </div>
  );
}
